import type { UserResponseArtifact } from "@/types/artifacts.ts";
import { useAtomValue } from "jotai";
import { selectAtom } from "jotai/utils";
import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { type AtomicRange, StreamHandler } from "./StreamHandler.ts";
import { streamReplyBuffersAtom } from "./streamReplyBuffer.ts";

/** Options for {@link useTextReveal}. */
type UseTextRevealOptions = {
  /**
   * Whether to animate the gradual character-by-character reveal at all (vs.
   * return `rawValue` instantly). This is the cosmetic-smoothing switch.
   */
  reveal?: boolean;
  /**
   * Whether `rawValue` is still arriving from a live server stream (the caller
   * sets this while it has a live reply-stream buffer for the field), as opposed
   * to the client-side cosmetic reveal this hook performs. While true, the reveal
   * keeps running so later deltas continue smoothly instead of stopping when
   * momentarily caught up; flipping it false lets the reveal drain and stop. Only
   * meaningful when `reveal` is true.
   */
  isServerStreaming?: boolean;
  /**
   * Spans of `rawValue` the reveal must cross in one tick (e.g. links),
   * cumulative, ascending, disjoint. Pass the full list every render while a
   * stream buffer exists — including after finalize, so a span still draining
   * reveals whole.
   */
  atomicRanges?: readonly AtomicRange[];
};

/** Stable no-op store subscription for the `reveal`-off path — nothing to subscribe to. */
const noopSubscribe =
  (_listener: () => void): (() => void) =>
  () => {};

/**
 * Generic character-by-character reveal for any string field, driving a per-leaf
 * {@link StreamHandler}. Stays domain-blind (it knows nothing about artifacts or
 * the stream-buffer atom); {@link useStreamedResponseText} is its one caller and
 * binds it to a `userResponse` artifact. Module-local by design — inline here
 * rather than exported so the split reads without a second file to open.
 *
 * @param rawValue the full value known so far. For a live stream this grows as
 *   deltas arrive while `isServerStreaming` is true, then lands authoritative.
 * @returns the gradually-revealed value when `reveal` is set, else `rawValue`.
 */
const useTextReveal = (
  rawValue: string,
  { reveal = false, isServerStreaming = false, atomicRanges }: UseTextRevealOptions = {},
): string => {
  // Lazily construct the singleton — a plain `new StreamHandler()` initializer
  // would build (and discard) a handler + pacer on every render.
  const streamHandlerRef = useRef<StreamHandler | null>(null);
  streamHandlerRef.current ??= new StreamHandler();
  const streamHandler = streamHandlerRef.current;

  // Only drive the reveal machinery when animating. With `reveal` off the hook
  // returns `rawValue` verbatim, so pushing content and running the 16ms reveal
  // timer (which re-renders every tick) would be pure waste — skip both.
  //
  // While animating, always push the latest value so the handler tracks the
  // server exactly — an empty authoritative finalize must still set content (and
  // flip the stream complete) rather than leaving the reveal timer ticking on
  // stale state. isServerStreaming is in the deps so a finalize with unchanged
  // text still flips the handler out of streaming mode (letting the reveal drain
  // and stop).
  useEffect(() => {
    if (!reveal) return;
    streamHandler.setContent(rawValue, !isServerStreaming, atomicRanges);
  }, [streamHandler, rawValue, isServerStreaming, reveal, atomicRanges]);

  // Subscribe to the reveal only when animating; otherwise use a no-op store
  // that echoes `rawValue`, so no interval starts.
  let subscribe = noopSubscribe;
  let getSnapshot = () => rawValue;
  if (reveal) {
    subscribe = streamHandler.subscribe;
    getSnapshot = streamHandler.getSnapshot;
  }

  return useSyncExternalStore(subscribe, getSnapshot);
};

/** Options for {@link useStreamedResponseText}. */
export type UseStreamedResponseTextOptions = {
  /**
   * Caller veto on the reveal, applied on top of the per-mount latch: pass
   * `false` to render the value whole even when this leaf did stream (e.g. a
   * surface that never animates). Defaults to `true`.
   */
  revealGate?: boolean;
};

/** What {@link useStreamedResponseText} returns for one `userResponse` artifact. */
export type StreamedResponseTextResult = {
  /** The revealed prose: gradually animated while streaming, whole otherwise. */
  revealedResponse: string;
  /** Whether there is any text yet — lets a caller hold a blank shell until the first delta. */
  hasResponse: boolean;
  /**
   * Whether this mount is animating the reveal: `revealGate` is set and this
   * leaf observed a live frame while the artifact was `inProgress`. Per-mount by
   * design — it resets on remount, so a settled reply renders whole rather than
   * replaying its stream. Drives the text-reveal animation only.
   *
   * Not a "text is still growing" signal: it is a one-way latch that stays set
   * for the life of the mount, long after the reply finalized. Affordances that
   * must wait for complete text (a copy button) read
   * {@link StreamedResponseTextResult.isTextStillArriving} instead.
   */
  isRevealing: boolean;
  /**
   * Whether the prose is still growing — the server is mid-stream, or it has
   * finalized and the reveal has yet to catch up. Unlike {@link isRevealing}
   * this settles to `false` once the text is whole, so an affordance that would
   * act on truncated content (copying a half-written command) can gate on it.
   *
   * Stays `true` across a momentary mid-stream catch-up, because the server leg
   * of the disjunction holds until the artifact leaves `inProgress`.
   */
  isTextStillArriving: boolean;
  /**
   * Whether a live stream buffer exists for this artifact anywhere in this
   * session. Buffers outlive mounts and are never cleared, so unlike
   * {@link isRevealing} this stays `true` after a remount of a reply that
   * already streamed. Callers use it to suppress a whole-block enter animation
   * on that remount while still animating a reply that genuinely arrived whole
   * (no buffer — e.g. a short reply that finalized without streaming).
   */
  streamedThisSession: boolean;
};

/**
 * Revealed prose for a `userResponse` artifact. The domain wrapper that binds an
 * artifact to the generic {@link useTextReveal} reveal: it chooses the raw value
 * (the live buffer while `inProgress`, the authoritative `response` once
 * finalized), tracks whether this mounted leaf actually saw a frame, and passes
 * the buffer's atomic ranges through.
 *
 * The reveal is gated on that per-mount latch, not buffer presence (which
 * outlives mounts, so a long-finished reply would wrongly re-animate on remount)
 * nor status alone (observable before the first frame's range metadata arrives).
 * Ranges pass through even after finalize so a span still draining reveals whole.
 */
export const useStreamedResponseText = (
  artifact: UserResponseArtifact,
  { revealGate = true }: UseStreamedResponseTextOptions = {},
): StreamedResponseTextResult => {
  // Read only this artifact's slice, so another reply's delta is a no-op here.
  const liveBuffer = useAtomValue(
    useMemo(() => selectAtom(streamReplyBuffersAtom, (m) => m[artifact.id]), [artifact.id]),
  );
  const isStreaming = artifact.status === "inProgress";
  // Buffer is authoritative while streaming; the finalized `response` wins after.
  const rawValue = isStreaming ? (liveBuffer?.text ?? "") : artifact.response;
  // Latch: once this leaf has seen a live frame it stays "did stream", so a
  // finalize (which clears `isStreaming`) doesn't retroactively ungate the
  // reveal. The render-phase write is deliberate and safe: it's an idempotent
  // one-way latch (StrictMode's double-invoke is a no-op), and deriving it from
  // committed state instead would lag a render and flicker the reveal gate.
  const streamedWhileMounted = useRef(false);
  if (isStreaming && liveBuffer !== undefined) streamedWhileMounted.current = true;
  const isRevealing = revealGate && streamedWhileMounted.current;
  const revealedResponse = useTextReveal(rawValue, {
    reveal: isRevealing,
    isServerStreaming: isStreaming,
    atomicRanges: liveBuffer?.atomicRanges,
  });
  return {
    revealedResponse,
    hasResponse: Boolean(rawValue),
    isRevealing,
    // Both legs are needed. The server leg alone would go false at finalize with
    // the reveal still draining; the reveal leg alone would blink false whenever
    // the reveal momentarily catches up to a slow stream.
    isTextStillArriving: isStreaming || revealedResponse !== rawValue,
    streamedThisSession: liveBuffer !== undefined,
  };
};
